import { BenefitItemType } from './benefit';

/*
  We have so many uses of this param that they clash sometimes.
  Use a more specific name for your query parameter if you can.
*/
export const actionQueryParam: string = 'action';

/*
  These are some more specific query parameters in use.
  They are imported from here so we can more easily find usage.
  The variable names are suffixed with 'QueryParam'.
*/
export const accountIdQueryParam: string = 'accountId';
export const appointmentIdQueryParam: string = 'appointmentId';
export const appointmentTypesQueryParam: string = 'appointmentTypes';
export const bundleActionQueryParam: string = 'bundleAction';
export const bundleIdQueryParam: string = 'bundleId';
export const businessUnitIdsQueryParam: string = 'businessUnitIds';
export const categoryColumnsQueryParam: string = 'categoryColumns';
export const codeColumnsQueryParam: string = 'codeColumns';
export const collapsedHeaderQueryParam: string = 'collapsedHeader';
export const cptCodesQueryParam = 'cptCodes';
export const createQueryParam: string = 'createQueryParam';
export const dateRangeQueryParam: string = 'dateRange';
export const dateTypeQueryParam: string = 'dateType';
export const eligibilityActionQueryParam: string = 'eligibilityAction';
export const eligibilityIdQueryParam: string = 'eligibilityId';
export const endDateQueryParam: string = 'endDate';
export const estimateTypesQueryParam: string = 'estimateTypes';
export const estimationIdQueryParam: string = 'estimationId';
export const estimationStatusQueryParam: string = 'friendlyStatus';
export const ezActionQueryParam: string = 'ezAction';
export const slideoutActionQueryParam: string = 'slideoutAction';
export const ezEstimationIdQueryParam: string = 'ezEstimationId';
export const idQueryParam: string = 'id';
export const layoutQueryParam: string = 'layout';
export const modelerComparisonIdQueryParam: string = 'comparisonId';
export const modelerWorkbookActionQueryParam: string = 'workbookAction';
export const mrfItemId: string = 'mrfItemId';
export const networkQueryParam: string = 'network';
export const patientIdQueryParam: string = 'patientId';
export const patientInfoConflictsQueryParam: string = 'patientInfoConflicts';
export const payerIdsQueryParam: string = 'payerIds';
export const paymentCredentialIdQueryParam: string = 'paymentCredentialId';
export const pdfSettingsQueryParam: string = 'pdfSettingsAction';
export const placesOfServiceQueryParam: string = 'placesOfService';
export const projectsDashboardTabQueryParam: string = 'tab';
export const projectStatusQueryParam: string = 'projectStatus';
export const providerNPIsQueryParam: string = 'providerNPIs';
export const savedViewQueryParam: string = 'savedView';
export const searchQueryParam: string = 'search';
export const searchEstimateTemplateQueryParam: string =
  'search-estimate-template';
export const serviceLocationIdsQueryParam: string = 'serviceLocationIds';
export const sortQueryParam: string = 'sort';
export const startDateQueryParam = 'startDate';
export const statusesQueryParam = 'statuses';
export const rollingDateRangeStartQueryParam = 'recurringFrom';
export const rollingDateRangeEndQueryParam = 'recurringTo';
export const viewByQueryParam = 'viewBy';
export const userIdsQueryParam = 'userIds';
export const showOnlyDifferingCodesQueryParam = 'showOnlyDifferingCodes';
export const workbookFiltersQueryParam = 'workbookFilters';
export const activeViewIdQueryParam = 'view';

/*
  These are the values that the query params could be set to.
  They are imported from here so we don't have spelling errors.
  The variable names are suffixed with 'Action'.
*/
export const activityTimelineAction: string = 'activity-timeline';
export const addComparisonAction: string = 'add-comparison';
export const adjustmentComparisonAction: string = 'adjustment-comparison';
export const adjustmentsSettingAction: string = 'adjustments-setting';
export const brandCreateAction: string = 'brand_create';
export const bundleEditAction: string = 'bundle-edit';
export const createProjectAction: string = 'create-project';
export const csvAction: string = 'csv';
export const deleteAction: string = 'delete';
export const duplicateAction: string = 'duplicate';
export const editAction: string = 'edit';
export const editNpiAction: string = 'editNpi';
export const editPatientAction: string = 'editPatient';
export const editRowVolumeAction: string = 'edit-row-volume';
export const eligibilityPopoutAction: string = 'eligibility-popout';
export const entireUtilizationAction: string = 'entire-utilization';
export const estimationAction: string = 'estimation';
export const existingTemplateAction: string = 'existing-template';
export const ezEstimationModalAction: string = 'ez-estimation-modal';
export const implementAction: string = 'implement';
export const investigateAutoEstimateAction: string =
  'investigate-auto-estimates';
export const noteCreateAction: string = 'note_create';
export const notesAction: string = 'notes';
export const previewAction: string = 'preview';
export const previewAndSendAction: string = 'pas';
export const proceduresAction: string = 'procedures';
export const processPaymentAction: string = 'process-payment';
export const renamingAction: string = 'renaming';
export const reorderingAction: string = 'reordering';
export const ruleComparisonAction: string = 'rule-comparison';
export const simpleEstimateViewAction: string = 'simple-estimate-view';
export const suggestionsAction: string = 'suggestions';
export const templateAction: string = 'template';
export const volumeEditAction: string = 'volume-edit';
export const workbookCreateAction: string = 'workbook-create';
export const filtersAction: string = 'filters';

/*
  Query params with a route scope should usually be built via a function.
  The function names are suffixed with 'ScopedQueryParam'.
*/
export function ezTreatmentScopedQueryParam(treatmentIndex: number): string {
  return `ezTreatment.${treatmentIndex}`;
}

export function actionScopedQueryParam(scope: string): string {
  return `${scope}.action`;
}

export function confirmScopedQueryParam(scope: string): string {
  return `${scope}.confirm`;
}

/*
  Path params get used slightly differently from query params.
  Having separate variables means we can reference the place the path param is defined.
  The variable names are suffixed with 'PathParam'.
*/
export const accountIdPathParam: string = 'accountId';
export const batchTypePathParam: string = 'batchType';
export const cdmIdPathParam: string = 'cdmId';
export const claimIdPathParam: string = 'claimId';
export const contactIdPathParam: string = 'contactId';
export const contractIdPathParam: string = 'contractId';
export const customDRGIdPathParam: string = 'customDRGId';
export const customScheduleIdPathParam: string = 'customScheduleId';
export const dataSourceIdPathParam: string = 'dataSourceId';
export const eligibilityIdPathParam: string = 'eligibilityId';
export const estimationIdPathParam: string = 'estimationId';
export const grouperCrosswalkIdPathParam: string = 'grouperCrosswalkId';
export const idPathParam: string = 'id';
export const patientIdPathParam: string = 'patientId';
export const payerIdPathParam: string = 'payerId';
export const phoneNumberPathParam: string = 'phoneNumber';
export const projectIdPathParam: string = 'projectId';
export const preventOpportunityGroupPathParam: string =
  'preventOpportunityGroup';
export const preventOpportunityIdPathParam: string = 'preventOpportunityId';
export const ruleIdPathParam: string = 'ruleId';
export const scheduleIdPathParam: string = 'scheduleId';
export const schemaIdPathParam: string = 'schemaId';
export const slugPathParam: string = 'slug';
export const taskTypePathParam: string = 'taskType';
export const threadIdPathParam: string = 'threadId';
export const typePathParam: string = 'type';
export const usageSetIdPathParam: string = 'usageSetId';
export const userIdPathParam: string = 'userId';
export const workbookIdPathParam: string = 'workbookId';

export function routerScopedPathParam(pathParam: string): string {
  return ':' + pathParam;
}

/*
  When we use something in usePathOrQuery, we should be confident
    it has both a path and a query param.
  So, we'll have a separate variable from the query param for that usage.
  The variable names are suffixed with 'PathOrQueryParam';
*/
export const estimationIdPathOrQueryParam: string = 'estimationId';
export const patientIdPathOrQueryParam: string = 'patientId';

/*
  We sometimes create tabs that use query params.
  The variable names are suffixed with 'TabQueryParam'.
*/
export const benefitTypeTabQueryParam: string = 'benefitType';
export const estimatesCreatedROITabQueryParam: string = 'estimatesCreated';
export const moneyCollectedROITabQueryParam: string = 'moneyCollected';
export const userCreatedROITabQueryParam: string = 'userCreated';

// We want a consistent pattern of saving tab params in local storage.
export function localStorageTabQueryParamKey(tabSetName: string): string {
  return `__TAB_QUERY_PARAM__${tabSetName}`;
}

/*
  These are the values that the tab query params could be set to.
  The variable names are suffixed with 'TabAction'.
*/

export const collectedInTabAction: string = 'collectedIn';
export const collectedOutTabAction: string = 'collectedOut';
export const prCollectedTabAction: string = 'prCollected';
export const quantityTabAction: string = 'quantity';
export const refundsTabAction: string = 'refunds';
export const coinsuranceTabAction: BenefitItemType = 'coinsurance';
export const copayTabAction: BenefitItemType = 'copay';
export const deductibleTabAction: BenefitItemType = 'deductible';
export const oopTabAction: BenefitItemType = 'oop';
export const limitationTabAction: BenefitItemType = 'limitation';

export function isValidRivetHealthUrl(urlString: string): boolean {
  /**
   * Check if a string is a valid URL in the rivethealth.com domain.
   * @param urlString - The URL string to validate
   * @returns True if valid rivethealth.com URL, false otherwise
   */
  try {
    // Parse the URL using the built-in URL constructor
    const parsedUrl = new URL(urlString);

    // Check if protocol is http or https
    if (parsedUrl.protocol !== 'http:' && parsedUrl.protocol !== 'https:') {
      return false;
    }

    // Get the hostname (domain) in lowercase
    const domain = parsedUrl.hostname.toLowerCase();

    // Check if it's exactly rivethealth.com or a subdomain
    if (domain === 'rivethealth.com') {
      return true;
    } else if (domain.endsWith('.rivethealth.com')) {
      return true;
    } else {
      return false;
    }
  } catch (error) {
    // URL constructor throws an error for invalid URLs
    return false;
  }
}
